/**
 * WebSocket service for real-time communication with the backend
 */

// WebSocket connection options
interface WebSocketOptions {
  onAlert: (alertData: any) => void;
  onSensorUpdate?: (sensorData: any) => void;
  onTestMessage?: (testData: any) => void;
  onTestSessionUpdate?: (sessionData: any) => void;
  onTestCommandResponse?: (commandData: any) => void;
  onError?: (error: Event) => void;
  onReconnect?: () => void;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
}

// WebSocket connection status
let socket: WebSocket | null = null;
let reconnectAttempts = 0;
let reconnectTimeout: NodeJS.Timeout | null = null;
let isConnecting = false;  // Flag to prevent duplicate connection attempts
let preferredUrl: string | null = null;  // Track the preferred connection URL

/**
 * Initialize WebSocket connection
 * @param options - WebSocket connection options
 * @returns Cleanup function
 */
export const initWebSocketConnection = (options: WebSocketOptions): (() => void) => {
  // Default options
  const {
    onAlert,
    onError,
    onReconnect,
    maxReconnectAttempts = 10,
    reconnectDelay = 3000,
  } = options;

  // Create WebSocket URL dynamically based on current location
  // But always use the backend port (8000) for WebSocket connections
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  
  // Extract hostname (without port) from current location
  const hostname = window.location.hostname;
  
  // Always use the configured WebSocket port from environment variables
  // This is our preferred URL - we'll ignore attempts to connect to other URLs
  const wsPort = process.env.REACT_APP_WS_PORT || '8000';
  preferredUrl = `${protocol}//${hostname}:${wsPort}/ws`;
  
  console.log('Primary WebSocket URL:', preferredUrl);

  // Connect to WebSocket server
  const connect = () => {
    // Prevent multiple simultaneous connection attempts
    if (isConnecting) {
      console.log('Connection attempt already in progress, skipping');
      return;
    }

    try {
      isConnecting = true;

      // Close existing connection if any
      if (socket) {
        socket.close();
        socket = null;
      }

      // Create new WebSocket connection to our preferred URL
      console.log('Connecting to WebSocket URL:', preferredUrl);
      socket = new WebSocket(preferredUrl!);

      // Connection opened
      socket.onopen = () => {
        console.log('WebSocket connection established');
        reconnectAttempts = 0;
        isConnecting = false;

        if (reconnectAttempts > 0 && onReconnect) {
          onReconnect();
        }
      };

      // Listen for messages
      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle different message types
          switch (data.type) {
            case 'new_alert':
              onAlert(data.data);
              break;
            case 'sensor_update':
              if (options.onSensorUpdate) {
                options.onSensorUpdate(data.data);
              }
              break;
            case 'test_message':
              if (options.onTestMessage) {
                options.onTestMessage(data);
              }
              break;
            case 'test_session_update':
              if (options.onTestSessionUpdate) {
                options.onTestSessionUpdate(data);
              }
              break;
            case 'test_command_response':
              if (options.onTestCommandResponse) {
                options.onTestCommandResponse(data);
              }
              break;
            case 'connected':
              console.log('WebSocket connected:', data.data.message);
              break;
            default:
              console.log('Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      // Connection closed
      socket.onclose = (event) => {
        // Check if this is a proxy-initiated connection to the wrong port
        const currentUrl = (event.target as WebSocket).url;
        if (currentUrl !== preferredUrl) {
          console.warn(`Ignoring connection close for non-preferred URL: ${currentUrl}`);
          isConnecting = false;
          return;
        }
        
        if (!event.wasClean) {
          console.warn('WebSocket connection closed unexpectedly');
          isConnecting = false;
          
          // Attempt to reconnect
          attemptReconnect();
        } else {
          console.log('WebSocket connection closed cleanly');
          isConnecting = false;
        }
      };

      // Connection error
      socket.onerror = (event) => {
        // Check if this is a proxy-initiated connection to the wrong port
        // If the URL doesn't match our preferred URL, ignore it
        const currentUrl = (event.target as WebSocket).url;
        if (currentUrl !== preferredUrl) {
          console.warn(`Ignoring connection error for non-preferred URL: ${currentUrl}`);
          isConnecting = false;
          return;
        }
        
        console.error('WebSocket error:', event);
        isConnecting = false;
        
        if (onError) {
          onError(event);
        }

        // Attempt to reconnect
        attemptReconnect();
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      isConnecting = false;
      attemptReconnect();
    }
  };

  // Attempt to reconnect with exponential backoff
  const attemptReconnect = () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
    }

    if (reconnectAttempts >= maxReconnectAttempts) {
      console.error(`Maximum reconnect attempts (${maxReconnectAttempts}) reached`);
      return;
    }

    reconnectAttempts++;
    
    // Exponential backoff with jitter
    const delay = reconnectDelay * Math.pow(1.5, reconnectAttempts - 1) * (0.9 + Math.random() * 0.2);
    
    console.log(`Attempting to reconnect in ${Math.round(delay / 1000)} seconds (attempt ${reconnectAttempts}/${maxReconnectAttempts})`);
    
    reconnectTimeout = setTimeout(() => {
      connect();
    }, delay);
  };

  // Initial connection
  connect();

  // Return cleanup function
  return () => {
    if (socket) {
      socket.close();
      socket = null;
    }

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    isConnecting = false;
    reconnectAttempts = 0;
    preferredUrl = null;
  };
};

/**
 * Send message to WebSocket server
 * @param message - Message to send
 * @returns True if message was sent, false otherwise
 */
export const sendWebSocketMessage = (message: any): boolean => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(message));
    return true;
  }
  return false;
};